import { ScheduleEntry, TimeRule } from '../types';
import { questionById, SETUP_QUESTIONS, SetupQuestion, SetupScreenId } from '../config/setupQuestions';
import { entryPart } from './minyanDay';

/**
 * Reading and saving set-up answers (Phase 6). Answers are not stored on their
 * own: they are the shul's normal time entries, so the guided set-up (A), the
 * year checklist (B), the Excel upload and the "All entries" list all edit the
 * same data, and resolveSchedule() works out every time from it.
 */

export type AnswerMode = 'own' | 'same' | 'none' | 'unset';

export interface Answer {
  mode: AnswerMode;
  /** The entries whose times this question shows (for 'same': the linked question's). */
  entries: ScheduleEntry[];
  /** 'none' because the question is optional and empty (nothing saved). */
  implicit?: boolean;
}

export interface LineDraft {
  /** Set when the line edits an existing entry. */
  id?: string;
  timeRule: TimeRule;
  note?: string;
  noteHe?: string;
  selichotFrom?: 'elul2' | 'firstNight';
}

function partOf(q: SetupQuestion) {
  return entryPart({ prayerType: q.prayer, night: q.night });
}

/** Active entries for this question's prayer and part of the day, tagged with its day. */
function taggedEntries(schedules: ScheduleEntry[], q: SetupQuestion): ScheduleEntry[] {
  return schedules.filter(
    (e) =>
      e.prayerType === q.prayer &&
      e.active !== false &&
      entryPart(e) === partOf(q) &&
      Array.isArray(e.dayTypes) &&
      e.dayTypes.includes(q.dayType)
  );
}

export function getAnswer(schedules: ScheduleEntry[], q: SetupQuestion, seen: Set<string> = new Set()): Answer {
  seen.add(q.id);
  const tagged = taggedEntries(schedules, q);
  const linked = q.sameAs && !seen.has(q.sameAs) ? questionById(q.sameAs) : undefined;
  const linkedAnswer = linked ? getAnswer(schedules, linked, seen) : undefined;

  if (tagged.some((e) => e.noMinyan)) return { mode: 'none', entries: [] };
  if (tagged.length > 0) {
    if (linked && linkedAnswer && linkedAnswer.entries.length > 0) {
      const ids = new Set(linkedAnswer.entries.map((e) => e.id));
      const sameSet = tagged.length === ids.size && tagged.every((e) => ids.has(e.id));
      if (sameSet) return { mode: 'same', entries: linkedAnswer.entries };
    }
    return { mode: 'own', entries: tagged };
  }
  if (q.kind === 'fallback') return { mode: 'same', entries: linkedAnswer?.entries ?? [] };
  if (q.kind === 'optional') return { mode: 'none', entries: [], implicit: true };
  return { mode: 'unset', entries: [] };
}

export const isAnswered = (a: Answer) => a.mode !== 'unset';

/** Remove this question's day from its entries (an entry only for this day is deleted). */
function clearQuestion(schedules: ScheduleEntry[], q: SetupQuestion): ScheduleEntry[] {
  const ids = new Set(taggedEntries(schedules, q).map((e) => e.id));
  const next: ScheduleEntry[] = [];
  for (const e of schedules) {
    if (!ids.has(e.id)) next.push(e);
    else if (e.dayTypes.length > 1) next.push({ ...e, dayTypes: e.dayTypes.filter((d) => d !== q.dayType) });
  }
  return next;
}

function newId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `e-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function buildEntry(q: SetupQuestion, line: LineDraft, keep?: ScheduleEntry): ScheduleEntry {
  const night = q.night && (q.prayer === 'maariv' || q.prayer === 'hakafot' || q.prayer === 'megillah') ? q.night : undefined;
  // Not trimmed: the editor saves on every key press.
  const note = line.note;
  const noteHe = line.noteHe;
  return {
    id: keep?.id ?? newId(),
    prayerType: q.prayer,
    dayTypes: [q.dayType],
    timeRule: line.timeRule,
    active: true,
    overrides: keep?.overrides ?? [],
    ...(night ? { night } : {}),
    ...(note ? { note } : {}),
    ...(noteHe ? { noteHe } : {}),
    ...(q.prayer === 'selichot' && line.selichotFrom ? { selichotFrom: line.selichotFrom } : {}),
    ...(q.replacesWeekday ? { overridesEntryId: '*' } : {}),
  };
}

/**
 * Save "own times". A line with an id edits that entry in place (it keeps its
 * other days, so linked days follow, and its "this date only" changes); lines
 * without an id become new entries; entries left out lose this day.
 */
export function setOwn(schedules: ScheduleEntry[], q: SetupQuestion, lines: LineDraft[]): ScheduleEntry[] {
  const taggedIds = new Set(taggedEntries(schedules, q).map((e) => e.id));
  const byId = new Map(lines.filter((l) => l.id && taggedIds.has(l.id)).map((l) => [l.id as string, l]));
  const next: ScheduleEntry[] = [];
  for (const e of schedules) {
    if (!taggedIds.has(e.id)) {
      next.push(e);
      continue;
    }
    const line = byId.get(e.id);
    if (line && !e.noMinyan) {
      const built = buildEntry(q, line, e);
      const shared = e.dayTypes.length > 1;
      const { note, noteHe, selichotFrom, ...rest } = e;
      next.push({
        ...rest,
        timeRule: built.timeRule,
        ...(built.note ? { note: built.note } : {}),
        ...(built.noteHe ? { noteHe: built.noteHe } : {}),
        ...(q.prayer === 'selichot'
          ? built.selichotFrom ? { selichotFrom: built.selichotFrom } : {}
          : selichotFrom ? { selichotFrom } : {}),
        ...(!shared && q.replacesWeekday ? { overridesEntryId: '*' } : {}),
      });
      continue;
    }
    const rest = e.dayTypes.filter((d) => d !== q.dayType);
    if (rest.length > 0) next.push({ ...e, dayTypes: rest });
  }
  const added = lines.filter((l) => !(l.id && byId.has(l.id))).map((l) => buildEntry(q, l));
  return [...next, ...added];
}

/** Save "Same as …". For a fallback question nothing is stored; otherwise the linked entries get this day. */
export function setSame(schedules: ScheduleEntry[], q: SetupQuestion): ScheduleEntry[] {
  const next = clearQuestion(schedules, q);
  if (q.kind === 'fallback' || !q.sameAs) return next;
  const linked = questionById(q.sameAs);
  if (!linked) return next;
  const ids = new Set(getAnswer(next, linked).entries.map((e) => e.id));
  return next.map((e) => (ids.has(e.id) && !e.dayTypes.includes(q.dayType) ? { ...e, dayTypes: [...e.dayTypes, q.dayType] } : e));
}

/** Save "No minyan" (for an optional question: just clear it). */
export function setNone(schedules: ScheduleEntry[], q: SetupQuestion): ScheduleEntry[] {
  const next = clearQuestion(schedules, q);
  if (q.kind === 'optional') return next;
  const night = q.night && (q.prayer === 'maariv' || q.prayer === 'hakafot' || q.prayer === 'megillah') ? q.night : undefined;
  return [
    ...next,
    {
      id: newId(),
      prayerType: q.prayer,
      dayTypes: [q.dayType],
      timeRule: { type: 'fixed', time: '00:00' },
      active: true,
      noMinyan: true,
      overrides: [],
      ...(night ? { night } : {}),
    },
  ];
}

/** Lines to start "Different time" from: this question's own entries, else the linked ones (as copies). */
export function startingLines(schedules: ScheduleEntry[], q: SetupQuestion): LineDraft[] {
  const answer = getAnswer(schedules, q);
  const own = answer.mode === 'own';
  return answer.entries.map((e) => ({
    ...(own ? { id: e.id } : {}),
    timeRule: e.timeRule,
    ...(e.note ? { note: e.note } : {}),
    ...(e.noteHe ? { noteHe: e.noteHe } : {}),
    ...(e.selichotFrom ? { selichotFrom: e.selichotFrom } : {}),
  }));
}

export interface SetupProgress {
  answered: number;
  total: number;
  missingByScreen: Partial<Record<SetupScreenId, number>>;
}

export function setupProgress(schedules: ScheduleEntry[], questions: SetupQuestion[] = SETUP_QUESTIONS): SetupProgress {
  const missingByScreen: Partial<Record<SetupScreenId, number>> = {};
  let answered = 0;
  for (const q of questions) {
    if (isAnswered(getAnswer(schedules, q))) answered += 1;
    else missingByScreen[q.screen] = (missingByScreen[q.screen] ?? 0) + 1;
  }
  return { answered, total: questions.length, missingByScreen };
}
